import type { IntlShape } from 'react-intl'

/**
 * Compact trip-planner transport durations for display.
 * e.g. "approximately 1 hour 20 minutes" → "1h 20m"
 * Approximation is shown by the caller as "(approx)", not a leading "~".
 */
export function compactTransportDuration(duration: string | undefined): string | undefined {
  if (!duration) return undefined

  let text = duration.trim()
  if (!text) return undefined

  text = text.replace(/^~+\s*/, '')
  text = text.replace(/^(approximately|approx\.?)\s+/i, '')
  text = text.replace(/\s*\(approx\.?\)$/i, '').trim()
  if (!text) return undefined

  const parsed = parseTransportDurationParts(text)
  if (!parsed) return text

  return formatCompactTransportDuration(parsed.hours, parsed.minutes)
}

export function formatCompactTransportDuration(hours: number, minutes: number): string {
  if (hours <= 0 && minutes <= 0) return '0m'
  if (hours <= 0) return `${minutes}m`
  if (minutes <= 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

/** Inline alternative label, e.g. "Train (4h 30m)". */
export function formatTransportAlternativeLabel(
  option: {
    label?: string
    type: string
    duration?: string
  },
  intl: IntlShape,
): string {
  const modeLabel = option.label ?? option.type
  const compactDuration = compactTransportDuration(option.duration)
  if (!compactDuration) return modeLabel

  return intl.formatMessage({
    id: 'Ny1Ut5_AulIrTFPpKSow',
    defaultMessage: '{modeLabel} ({duration})',
    description: 'Inline alternative transport mode label with compact duration in the trip planner',
  }, {
    modeLabel,
    duration: compactDuration,
  })
}

function parseTransportDurationParts(
  text: string,
): { hours: number; minutes: number } | undefined {
  const compact = text.match(
    /^(?:(\d+)\s*h(?:ours?)?)?(?:\s*(\d+)\s*m(?:in(?:ute)?s?)?)?$/i,
  )
  if (compact && (compact[1] !== undefined || compact[2] !== undefined)) {
    return {
      hours: Number(compact[1] ?? 0),
      minutes: Number(compact[2] ?? 0),
    }
  }

  const minutesOnly = text.match(/^(\d+)\s*m(?:in(?:ute)?s?)?$/i)
  if (minutesOnly) {
    return { hours: 0, minutes: Number(minutesOnly[1]) }
  }

  const natural = text.match(
    /^(?:(\d+)\s*(?:hours?|hrs?|hr))?(?:\s*(?:and\s*)?(\d+)\s*(?:minutes?|mins?|min))?$/i,
  )
  if (natural && (natural[1] !== undefined || natural[2] !== undefined)) {
    return {
      hours: Number(natural[1] ?? 0),
      minutes: Number(natural[2] ?? 0),
    }
  }

  return undefined
}
